'use client'

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import VideoPlayer from './video-player'

export interface Step {
  id: number
  title: string
  description: string
  videoSrc?: string
  videoPoster?: string
}

interface InteractiveStepsProps {
  steps: Step[]
  layout?: 'steps-left' | 'steps-right'
  className?: string
}

export default function InteractiveSteps({ 
  steps, 
  layout = 'steps-left',
  className = ""
}: InteractiveStepsProps) {
  const [activeStep, setActiveStep] = useState(1)
  const [prevStep, setPrevStep] = useState(1)
  const currentStep = steps.find(step => step.id === activeStep) || steps[0]
  const handleStepChange = (stepId: number) => {
    setPrevStep(activeStep)
    setActiveStep(stepId)
  }

  const StepsContent = () => (
    <div className="hidden self-center md:block">
      <div className="flex items-center justify-start">
        <div className="flex w-full max-w-[530px] flex-col gap-2">
          {steps.map((step) => (
            <button
              key={step.id}
              onClick={() => handleStepChange(step.id)}
              className={cn(
                "w-full rounded-2xl p-6 text-left transition-all duration-200",
                activeStep === step.id 
                  ? "bg-background opacity-100" 
                  : "bg-transparent opacity-60 hover:opacity-80"
              )}
            >
              
              <div className="flex items-start gap-4">
                <span 
                  className={cn(
                    "font-medium text-lg transition-colors duration-200",
                    activeStep === step.id 
                      ? "bg-gradient-primary bg-clip-text text-transparent"
                      : "text-muted-foreground"
                  )}
                >
                  {step.id.toString().padStart(2, '0')}.
                </span>
                <div className="flex-1">
                  <h3 
                    className={cn(
                      "font-medium text-lg transition-colors duration-200",
                      activeStep === step.id ? "text-foreground" : "text-muted-foreground"
                    )}
                  >
                    {step.title}
                  </h3>
                  <AnimatePresence mode="wait">
                    {(activeStep === step.id || prevStep === step.id) && (
                      <motion.p 
                        key={`${step.id}-${activeStep === step.id ? 'active' : 'prev'}`}
                        className="text-muted-foreground mt-2"
                        initial={activeStep === step.id ? { height: 0, opacity: 0 } : { height: 'auto', opacity: 1 }}
                        animate={activeStep === step.id ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                          duration: 0.2,
                          ease: "easeOut"
                        }}
                        style={{ overflow: 'hidden' }}
                      >
                        {step.description}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )

  const PlaceholderContent = () => {
    return (
      <div className="hidden md:block">
        <div className="relative my-12 aspect-[0.939] w-full">
          <div className="absolute inset-0 flex aspect-[0.939] w-full items-center justify-center">
            <div className="aspect-[0.939] w-full rounded-2xl p-3">
              <VideoPlayer 
                src={currentStep.videoSrc}
                poster={currentStep.videoPoster}
                autoPlay={false}
                autoPlayOnVisible={true}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  const MobileContent = () => (
    <div className="md:hidden">
      <div className="flex flex-col items-center gap-6">
        {steps.map((step) => (
          <div key={step.id} className="w-full rounded-2xl border border-border bg-background">
            <div className="aspect-[0.939] w-full rounded-2xl bg-background p-4">
              <VideoPlayer 
                src={step.videoSrc}
                poster={step.videoPoster}
                autoPlay={false}
                autoPlayOnVisible={true}
                aspectRatio="aspect-[0.939]"
              />
            </div>
            <div className="flex flex-col gap-2 p-5">
              <div className="flex items-center gap-3">
                <h3 className="font-medium text-lg text-foreground leading-tight tracking-tight">
                  {step.title}
                </h3>
              </div>
              <p className="text-muted-foreground leading-relaxed tracking-tight">
                {step.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <div className={cn("grid gap-6 md:grid-cols-2", className)}>
      {layout === 'steps-left' ? (
        <>
          <StepsContent />
          <PlaceholderContent />
        </>
      ) : (
        <>
          <PlaceholderContent />
          <StepsContent />
        </>
      )}
      <MobileContent />
    </div>
  )
}
